import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Tab, Tabs, Typography, alpha } from '@mui/material';
import {
    SyntheticEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    Navigate,
    Outlet,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    getNewAddedItemsTotal,
    getNewCatalogItemsTotal,
    getNewInventoryItemsTotal,
    getNewPropertyCatalogItemsTotal,
    getSelectedMarketplaceItemsTotal,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { fetchCatalogItems } from 'entities/CatalogItem/model/services/fetchCatalogItems/fetchCatalogItems';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { fetchCategories } from 'entities/Category';
import { getSelectCategoryId } from 'entities/Category/model/selectors/categorySelectors';
import {
    fetchCustomListDetail,
    getCustomListDetail,
} from 'entities/CustomList';
import { getCanAddMarketplaceItemsToOrder } from 'entities/Order/model/selectors/ordersPermissions';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { fetchPropertyVendorsList } from 'entities/Vendors';
import { AddItemsToCustomListButton } from 'features/AddItemsToCustomList';
import { mapCustomListItemsToSelection } from 'features/AddItemsToCustomList/lib/mapCustomListItemsToSelection';
import {
    AddItemsBackFromSearchButton,
    SearchCatalogItemsField,
} from 'features/SearchCatalogItems';
import { getSearchCatalogItemIsActive } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import {
    getRouteCatalog,
    getRouteCatalogList,
    getRouteCatalogListAddItemsCategories,
    getRouteCatalogListAddItemsInventory,
    getRouteCatalogListAddItemsMarketplace,
    getRouteCatalogRoot,
} from 'shared/const/router';
import {
    CatalogIdentityProvider,
    getCurrentTabFromPathname,
    useIsOrdersAddItemsMarketplace,
} from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { BlockLeaveAddFlowModal } from 'shared/ui/BlockLeaveAddFlowModal';
import { ButtonTabs } from 'shared/ui/ButtonTabs';
import { ReviewCatalogSelectionFooter } from 'shared/ui/ReviewCatalogSelectionFooter';
import { revertCatalogStores } from 'shared/ui/store/resetStoresAction';

const CustomListAddItemsPageContent = memo(() => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { propertyId: routePropertyId = '', listId = '' } = useParams();
    const [showBlockModal, setShowBlockModal] = useState(false);

    const isMarketplaceTab = useIsOrdersAddItemsMarketplace();
    const isSearchActive = useSelector(getSearchCatalogItemIsActive);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const selectedPropertyCatalogId = useSelector(getSelectedPropertyCatalogId);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const newAddedItemsTotal = useSelector(getNewAddedItemsTotal);
    const catalogCount = useSelector(getNewPropertyCatalogItemsTotal);
    const marketplaceCount = useSelector(getSelectedMarketplaceItemsTotal);
    const newCatalogItemsTotal = useSelector(getNewCatalogItemsTotal);
    const newInventoryItemsTotal = useSelector(getNewInventoryItemsTotal);
    const canAddMarketplaceItems = useSelector(
        getCanAddMarketplaceItemsToOrder,
    );
    const listDetail = useSelector(getCustomListDetail);

    const propertyId = routePropertyId || selectedPropertyId;
    const catalogId = selectedPropertyCatalogId;
    const currentTab = getCurrentTabFromPathname(location.pathname);
    const outerTab = currentTab === 'marketplace' ? 'marketplace' : 'catalog';

    useInitialEffect(() => {
        if (listId) {
            dispatch(fetchCustomListDetail({ listId }));
        }
    });

    useEffect(() => {
        if (catalogId) {
            dispatch(fetchCategories(catalogId));
        }
    }, [catalogId, dispatch]);

    useEffect(() => {
        if (propertyId) {
            dispatch(fetchPropertyVendorsList(propertyId));
        }
    }, [propertyId, dispatch]);

    useEffect(() => {
        if (!listDetail?.items) {
            return;
        }
        dispatch(
            catalogItemActions.hydrateFromCustomList(
                mapCustomListItemsToSelection(listDetail.items),
            ),
        );
    }, [dispatch, listDetail]);

    useEffect(() => {
        if (selectedCategoryId && !isMarketplaceTab && catalogId) {
            dispatch(
                fetchCatalogItems({
                    data: {
                        catalogId,
                        categoryId: selectedCategoryId,
                    },
                }),
            );
        }
    }, [catalogId, dispatch, isMarketplaceTab, selectedCategoryId]);

    useEffect(
        () => () => {
            dispatch(revertCatalogStores());
        },
        [dispatch],
    );

    const goBack = useCallback(() => {
        if (newAddedItemsTotal > 0) {
            setShowBlockModal(true);
            return;
        }
        if (propertyId && listId) {
            navigate(getRouteCatalogList(propertyId, listId));
        }
    }, [listId, navigate, newAddedItemsTotal, propertyId]);

    const onOuterTabChange = (_: SyntheticEvent, value: string) => {
        if (!propertyId || !listId) {
            return;
        }
        if (value === 'catalog') {
            navigate(getRouteCatalogListAddItemsCategories(propertyId, listId));
        } else {
            navigate(
                getRouteCatalogListAddItemsMarketplace(propertyId, listId),
            );
        }
    };

    const categoriesLabel = t('Categories');
    const inventoryLabel = t('Inventory');

    const innerTabs = useMemo(
        () => [
            { label: categoriesLabel, count: newCatalogItemsTotal },
            { label: inventoryLabel, count: newInventoryItemsTotal },
        ],
        [
            categoriesLabel,
            inventoryLabel,
            newCatalogItemsTotal,
            newInventoryItemsTotal,
        ],
    );

    const currentInnerTab =
        currentTab === 'inventory' ? inventoryLabel : categoriesLabel;

    const onInnerTabChange = (value: string) => {
        if (!propertyId || !listId) {
            return;
        }
        if (value === inventoryLabel) {
            navigate(getRouteCatalogListAddItemsInventory(propertyId, listId));
        } else {
            navigate(getRouteCatalogListAddItemsCategories(propertyId, listId));
        }
    };

    if (!catalogId || !propertyId || !listId) {
        return (
            <Navigate
                to={
                    propertyId
                        ? getRouteCatalog(propertyId)
                        : getRouteCatalogRoot()
                }
                replace
            />
        );
    }

    const isAddItemsIndex =
        location.pathname.endsWith('/add-items') ||
        location.pathname.endsWith('/add-items/');

    if (isAddItemsIndex) {
        return (
            <Navigate
                to={getRouteCatalogListAddItemsCategories(propertyId, listId)}
                replace
            />
        );
    }

    const listName = listDetail?.name || t('customLists.listFallbackName');

    return (
        <Box
            data-testclass="custom-list-add-items"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '26px',
                }}
            >
                <Button
                    onClick={goBack}
                    startIcon={
                        <ArrowBack sx={{ width: '24px', height: '24px' }} />
                    }
                    sx={(theme) => ({
                        color: alpha(theme.palette.common.black, 0.5),
                        padding: 0,
                        ':hover': { bgcolor: 'transparent' },
                        '.MuiButton-startIcon': { marginLeft: 0 },
                    })}
                    data-testclass="custom-list-add-items-back"
                >
                    <Typography variant="body2">{listName}</Typography>
                </Button>
            </Box>

            <Typography marginBottom="14px" variant="h1">
                {t('customLists.addItemsToList')}
            </Typography>

            <Tabs
                value={outerTab}
                onChange={onOuterTabChange}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    marginBottom: '16px',
                }}
            >
                <Tab
                    label={
                        catalogCount > 0
                            ? `${t('Property Catalog')} (${catalogCount})`
                            : t('Property Catalog')
                    }
                    value="catalog"
                    sx={{ textTransform: 'none' }}
                />
                {canAddMarketplaceItems ? (
                    <Tab
                        label={
                            marketplaceCount > 0
                                ? `${t(
                                      'RE-OPS Marketplace',
                                  )} (${marketplaceCount})`
                                : t('RE-OPS Marketplace')
                        }
                        value="marketplace"
                        sx={{ textTransform: 'none' }}
                    />
                ) : null}
            </Tabs>

            {!isMarketplaceTab ? (
                <Box
                    marginBottom="16px"
                    display="flex"
                    alignItems="center"
                    gap="16px"
                >
                    <ButtonTabs
                        tabs={innerTabs}
                        value={currentInnerTab}
                        setValue={onInnerTabChange}
                        height="40px"
                        testId="custom-list-add-item-tabs-property-catalog"
                    />
                    <Box
                        flex={1}
                        borderRadius="8px"
                        border="1px solid"
                        sx={{ borderColor: 'divider' }}
                    >
                        <SearchCatalogItemsField
                            catalogId={catalogId}
                            hideBorder
                            isInventory={currentTab === 'inventory'}
                        />
                    </Box>
                </Box>
            ) : null}

            <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
                <Outlet />
            </Box>

            <Box sx={{ flexShrink: 0 }}>
                <ReviewCatalogSelectionFooter
                    selectedCount={newAddedItemsTotal}
                    leadingAction={
                        isSearchActive ? (
                            <AddItemsBackFromSearchButton
                                isInventory={currentTab === 'inventory'}
                            />
                        ) : undefined
                    }
                >
                    <AddItemsToCustomListButton
                        listId={listId}
                        propertyId={propertyId}
                    />
                </ReviewCatalogSelectionFooter>
            </Box>

            <BlockLeaveAddFlowModal
                onCloseBlockModal={() => setShowBlockModal(false)}
                showBlockModal={showBlockModal}
                title={t('customLists.cancelAddItemsTitle')}
                message={t('customLists.cancelAddItemsMessage')}
                submitButtonText={t('customLists.cancelAddItemsConfirm')}
                onSubmitModal={() => {
                    setShowBlockModal(false);
                    navigate(getRouteCatalogList(propertyId, listId));
                }}
            />
        </Box>
    );
});

const CustomListAddItemsPage = memo(() => (
    <CatalogIdentityProvider value="lists-add-items">
        <CustomListAddItemsPageContent />
    </CatalogIdentityProvider>
));

export default CustomListAddItemsPage;
